import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

const LAST_CHECKED_KEY = 'sd.swUpdate.lastCheckedAt';

export type SwCheckStatus =
  | 'idle'
  | 'checking'
  | 'up-to-date'
  | 'update-available'
  | 'updating'
  | 'unavailable'
  | 'error';

@Injectable({ providedIn: 'root' })
export class SwUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly platformId = inject(PLATFORM_ID);

  readonly status = signal<SwCheckStatus>('idle');
  readonly lastCheckedAt = signal<Date | null>(this.readLastChecked());
  readonly errorMessage = signal<string | null>(null);

  init(): void {
    if (!this.swUpdate.isEnabled) {
      this.status.set('unavailable');
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        this.status.set('update-available');
      });
  }

  async checkForUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      this.status.set('unavailable');
      return;
    }

    this.status.set('checking');
    this.errorMessage.set(null);
    try {
      const hasUpdate = await this.swUpdate.checkForUpdate();
      this.recordCheck();
      this.status.set(hasUpdate ? 'update-available' : 'up-to-date');
    } catch (err) {
      this.errorMessage.set(this.toMessage(err));
      this.status.set('error');
    }
  }

  async applyUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) return;

    this.status.set('updating');
    this.errorMessage.set(null);
    try {
      await this.swUpdate.activateUpdate();
      document.location.reload();
    } catch (err) {
      this.errorMessage.set(this.toMessage(err));
      this.status.set('error');
    }
  }

  private recordCheck(): void {
    const now = new Date();
    this.lastCheckedAt.set(now);
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(LAST_CHECKED_KEY, now.toISOString());
      } catch {}
    }
  }

  private readLastChecked(): Date | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      const raw = localStorage.getItem(LAST_CHECKED_KEY);
      if (!raw) return null;
      const d = new Date(raw);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }

  private toMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    return 'Eroare necunoscută';
  }
}
